import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Editor from "@monaco-editor/react";
import {
    Box,
    Typography,
    Card,
    Button,
    Chip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Stack, // ✅ Added missing import
} from "@mui/material";

import CodeIcon from "@mui/icons-material/Code";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { executeCode } from "./services/Judge0Api";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: "120px",
    paddingBottom: "80px",
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: "80px",
    },
}));

const CompilerCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "30px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const OutputContainer = styled(Box)(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    minHeight: "150px",
    whiteSpace: "pre-wrap",
    fontFamily: "'Fira Code', 'Courier New', monospace",
    border: "1px solid rgba(255,255,255,0.05)",
    fontSize: "0.95rem",
}));




const defaultCode = {
    java: `public class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello, World!");\n    }\n}`,
    cpp: `#include <iostream>\nusing namespace std;\n\nint main() {\n    cout << "Hello, World!";\n    return 0;\n}`,
    python: `print("Hello, World!")`,
    javascript: `console.log("Hello, World!");`,
};

/* ✅ MAIN COMPONENT */
export default function OnlineCompiler() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [language, setLanguage] = useState("java");
    const [code, setCode] = useState(defaultCode.java);
    const [output, setOutput] = useState("");
    const [loading, setLoading] = useState(false);
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const runCode = async () => {
        setLoading(true);
        setOutput("Compiling and executing...");
        try {
            const result = await executeCode(language, code);
            setOutput(result || "Code executed successfully with no output.");
        } catch (error) {
            setOutput("Error: Could not connect to the execution server.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageWrapper>

            {/* 🏆 Section Banner */}
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 800,
                        //backgroundColor: "goldenrod",
                        color: "goldenrod",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    CODE COMPILER
                </Typography>
            </Box>

            <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, md: 4 } }}>
                <CompilerCard>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "goldenrod", display: "flex", alignItems: "center", gap: 1 }}>
                            <CodeIcon /> Editor
                        </Typography>

                        {/* ✅ Stack component used here now has its import */}
                        <Stack direction="row" spacing={2} sx={{ width: isMobile ? "100%" : "auto" }}>
                            <FormControl size="small" sx={{ minWidth: 150, bgcolor: "rgba(255,255,255,0.05)", borderRadius: 1 }}>
                                <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>Language</InputLabel>
                                <Select
                                    value={language}
                                    label="Language"
                                    onChange={(e) => {
                                        setLanguage(e.target.value);
                                        setCode(defaultCode[e.target.value]);
                                    }}
                                    sx={{ color: "white", ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" } }}
                                >
                                    <MenuItem value="java">Java</MenuItem>
                                    <MenuItem value="cpp">C++</MenuItem>
                                    <MenuItem value="python">Python</MenuItem>
                                    <MenuItem value="javascript">JavaScript</MenuItem>
                                </Select>
                            </FormControl>

                            <Button
                                variant="contained"
                                startIcon={loading ? null : <PlayArrowIcon />}
                                onClick={runCode}
                                disabled={loading}
                                sx={{
                                    backgroundColor: "goldenrod",
                                    color: "#100720",
                                    fontWeight: 800,
                                    px: 4,
                                    borderRadius: "8px",
                                    "&:hover": { backgroundColor: "#e0b200" },
                                }}
                            >
                                {loading ? "Running..." : "Run"}
                            </Button>
                        </Stack>
                    </Box>

                    <Box sx={{ borderRadius: "8px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)" }}>
                        <Editor
                            height={isMobile ? "300px" : "450px"}
                            language={language}
                            value={code}
                            theme="vs-dark"
                            onChange={(value) => setCode(value)}
                            options={{
                                fontSize: 16,
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                                fontFamily: "'Fira Code', monospace",
                            }}
                        />
                    </Box>
                </CompilerCard>

                <CompilerCard>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: "goldenrod" }}>
                        Console Output
                    </Typography>
                    <OutputContainer>
                        {output || "// Click 'Run' to see results..."}
                    </OutputContainer>
                </CompilerCard>
            </Box>

            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
